import type { Timestamp } from 'firebase/firestore';

/**
 * Supported file types for the library
 */
export type FileType = 'pdf' | 'doc' | 'video' | 'audio' | 'link' | 'image' | 'other';

/**
 * Sort options for library files
 */
export type LibrarySortOption =
  | 'date_desc' // Date added (newest first) - default
  | 'date_asc' // Date added (oldest first)
  | 'title_asc' // Title (A-Z)
  | 'title_desc' // Title (Z-A)
  | 'views_desc' // Most viewed
  | 'downloads_desc'; // Most downloaded

/**
 * A tag that can be applied to library files
 */
export interface FileTag {
  id: string;
  name: string;
  color?: string; // Optional hex color for visual distinction
  usageCount: number; // Number of files using this tag
  createdAt: Timestamp;
  createdBy: string;
  createdByName?: string;
}

/**
 * Input for creating a new tag
 */
export interface CreateTagInput {
  name: string;
  color?: string;
}

/**
 * A reusable default thumbnail that admins upload for use across materials
 */
export interface DefaultThumbnail {
  id: string;
  name: string;
  fileType: FileType;
  aspectRatio: 'square' | 'wide';
  imageUrl: string;
  storagePath: string;
  createdAt: Timestamp;
  createdBy: string;
  createdByName?: string;
}

/**
 * Enhanced library file with all metadata
 */
export interface LibraryFile {
  id: string;
  title: string;
  description?: string; // Optional description
  url: string; // Firebase Storage URL or external URL
  storagePath?: string; // Path in Firebase Storage (for uploaded files)
  fileName?: string; // Original file name (for uploaded files)
  fileSize?: number; // File size in bytes
  mimeType?: string; // MIME type for auto-detection
  type: FileType; // Detected or assigned file type
  tags: string[]; // Array of tag IDs
  author: string; // Creator's UID
  authorName?: string; // Display name of creator
  createdAt: Timestamp | null;
  updatedAt?: Timestamp; // Last update time
  viewCount: number; // View tracking
  downloadCount: number; // Download tracking
  // Square thumbnail fields (used on cards)
  thumbnailUrl?: string;
  thumbnailStoragePath?: string;
  defaultThumbnailId?: string;
  // Wide thumbnail fields (used on hero/featured)
  wideThumbnailUrl?: string;
  wideThumbnailStoragePath?: string;
  wideDefaultThumbnailId?: string;
  // Community posting fields
  postedToCommunity?: boolean;
  communityMessageId?: string; // Reference to the community message
  postedAt?: Timestamp;
  postedBy?: string; // UID of who posted to community
  // Featured material fields
  featuredAt?: Timestamp;
  featuredBy?: string;
  // Document-to-PDF conversion fields
  conversionStatus?: 'processing' | 'completed' | 'failed';
  convertedFrom?: string;
  conversionError?: string;
}

/**
 * Input for creating/updating a library file
 */
export interface LibraryFileInput {
  title: string;
  description?: string;
  url?: string; // For URL-based entries
  file?: File; // For uploaded files
  type?: FileType; // Override auto-detected type
  tags?: string[];
}

/**
 * File statistics stored separately for efficient updates
 */
export interface FileStats {
  viewCount: number;
  downloadCount: number;
  lastViewedAt?: Timestamp;
  lastDownloadedAt?: Timestamp;
}

/**
 * MIME type to FileType mapping for auto-detection
 */
export const MIME_TYPE_MAP: Record<string, FileType> = {
  // PDF
  'application/pdf': 'pdf',

  // Documents
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'doc',
  'application/vnd.ms-excel': 'doc',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'doc',
  'application/vnd.ms-powerpoint': 'doc',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'doc',
  'text/plain': 'doc',
  'application/rtf': 'doc',
  'text/csv': 'doc',
  'application/csv': 'doc',

  // Video
  'video/mp4': 'video',
  'video/webm': 'video',
  'video/ogg': 'video',
  'video/quicktime': 'video',
  'video/x-msvideo': 'video',

  // Audio
  'audio/mpeg': 'audio',
  'audio/mp3': 'audio',
  'audio/wav': 'audio',
  'audio/ogg': 'audio',
  'audio/webm': 'audio',
  'audio/aac': 'audio',
  'audio/m4a': 'audio',
  'audio/x-m4a': 'audio',

  // Images
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'image/svg+xml': 'image',
  'image/bmp': 'image',
};

/**
 * File extension to FileType mapping (fallback)
 */
export const EXTENSION_TYPE_MAP: Record<string, FileType> = {
  // PDF
  '.pdf': 'pdf',

  // Documents
  '.doc': 'doc',
  '.docx': 'doc',
  '.xls': 'doc',
  '.xlsx': 'doc',
  '.ppt': 'doc',
  '.pptx': 'doc',
  '.txt': 'doc',
  '.rtf': 'doc',
  '.csv': 'doc',

  // Video
  '.mp4': 'video',
  '.webm': 'video',
  '.mov': 'video',
  '.avi': 'video',
  '.mkv': 'video',

  // Audio
  '.mp3': 'audio',
  '.wav': 'audio',
  '.ogg': 'audio',
  '.m4a': 'audio',
  '.aac': 'audio',

  // Images
  '.jpg': 'image',
  '.jpeg': 'image',
  '.png': 'image',
  '.gif': 'image',
  '.webp': 'image',
  '.svg': 'image',
};

/**
 * Display configuration for each file type
 */
export interface FileTypeConfig {
  label: string;
  icon: string; // Lucide icon name
  color: string; // Tailwind color class
  bgColor: string; // Tailwind bg color class
}

export const FILE_TYPE_CONFIG: Record<FileType, FileTypeConfig> = {
  pdf: {
    label: 'Document',
    icon: 'FileText',
    color: 'text-red-500',
    bgColor: 'bg-red-50',
  },
  doc: {
    label: 'Document',
    icon: 'FileText',
    color: 'text-[#8e4585]',
    bgColor: 'bg-[#f8e8f8]',
  },
  video: {
    label: 'Video',
    icon: 'Play',
    color: 'text-[#8e4585]',
    bgColor: 'bg-[#f8e8f8]',
  },
  audio: {
    label: 'Audio',
    icon: 'Music',
    color: 'text-green-500',
    bgColor: 'bg-green-50',
  },
  link: {
    label: 'Link',
    icon: 'Link2',
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-50',
  },
  image: {
    label: 'Image',
    icon: 'Image',
    color: 'text-pink-500',
    bgColor: 'bg-pink-50',
  },
  other: {
    label: 'Other',
    icon: 'File',
    color: 'text-slate-500',
    bgColor: 'bg-slate-50',
  },
};

/**
 * Detect file type from MIME type or file extension
 */
export function detectFileType(mimeType?: string, fileName?: string): FileType {
  // Try MIME type first
  if (mimeType && MIME_TYPE_MAP[mimeType]) {
    return MIME_TYPE_MAP[mimeType];
  }

  // Fall back to file extension
  if (fileName) {
    const ext = fileName.toLowerCase().slice(fileName.lastIndexOf('.'));
    if (EXTENSION_TYPE_MAP[ext]) {
      return EXTENSION_TYPE_MAP[ext];
    }
  }

  return 'other';
}

/**
 * Detect file type from URL (for external links)
 */
export function detectFileTypeFromUrl(url: string): FileType {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname.toLowerCase();

    // Check common video platforms
    if (
      urlObj.hostname.includes('youtube.com') ||
      urlObj.hostname.includes('youtu.be') ||
      urlObj.hostname.includes('vimeo.com')
    ) {
      return 'video';
    }

    // Check for file extension in path
    for (const [ext, type] of Object.entries(EXTENSION_TYPE_MAP)) {
      if (path.endsWith(ext)) {
        return type;
      }
    }

    // Default to link for external URLs
    return 'link';
  } catch {
    return 'link';
  }
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
